import React, { useEffect, useState } from 'react';
import { useDemoMode } from '../lib/demoMode';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon } from 'lucide-react';
import { toast } from 'sonner';
export function DemoOverlay() {
  const {
    isActive,
    currentStep,
    steps,
    nextStep,
    skipDemo
  } = useDemoMode();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const currentStepData = steps[currentStep];
  useEffect(() => {
    if (!isActive || !currentStepData) return;
    // Handle navigation
    if (currentStepData.actionType === 'navigate' && currentStepData.route) {
      // In a real app with routing, navigate here
      // For now, we'll just wait for the element to appear
    }
    // Handle toast
    if (currentStepData.actionType === 'toast' && currentStepData.toastMessage) {
      toast.success(currentStepData.toastMessage, {
        duration: 4000
      });
    }
    // Find and highlight target element
    const findElement = () => {
      const element = document.querySelector(currentStepData.targetSelector) as HTMLElement;
      if (element) {
        setTargetElement(element);
        setTargetRect(element.getBoundingClientRect());
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };
    // Try immediately and then retry after a delay
    findElement();
    const timer = setTimeout(findElement, 300);
    // Update on scroll/resize
    const updateRect = () => {
      if (targetElement) {
        setTargetRect(targetElement.getBoundingClientRect());
      }
    };
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [isActive, currentStep, currentStepData, targetElement]);
  if (!isActive || !currentStepData) return null;
  const popoverPosition = targetRect ? {
    top: targetRect.bottom + 20,
    left: targetRect.left + targetRect.width / 2
  } : {
    top: '50%',
    left: '50%'
  };
  return <AnimatePresence data-id="element-579">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[90] pointer-events-none" data-id="element-580">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" data-id="element-581" />
        {/* Target Highlight */}
        {targetRect && <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="absolute pointer-events-none" style={{
        top: targetRect.top - 8,
        left: targetRect.left - 8,
        width: targetRect.width + 16,
        height: targetRect.height + 16
      }} data-id="element-582">
            <div className="w-full h-full border-4 border-blue-500 rounded-lg shadow-2xl shadow-blue-500/50 animate-pulse" data-id="element-583" />
          </motion.div>}
        {/* Popover Card */}
        <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: -20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.2
      }} className="absolute pointer-events-auto" style={{
        top: popoverPosition.top,
        left: popoverPosition.left,
        transform: 'translateX(-50%)',
        maxWidth: '400px'
      }} data-id="element-584">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden" data-id="element-585">
            {/* Header */}
            <div className="bg-sprBlue px-4 py-3 flex items-center justify-between" data-id="element-586">
              <div className="flex items-center gap-2" data-id="element-587">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-588" />
                <span className="text-sm font-semibold text-white font-sans" data-id="element-589">
                  Step {currentStep + 1} of {steps.length}
                </span>
              </div>
              <button onClick={skipDemo} className="text-white/80 hover:text-white transition-colors" data-id="element-590">
                <XIcon className="w-4 h-4" data-id="element-591" />
              </button>
            </div>
            {/* Content */}
            <div className="p-5" data-id="element-592">
              <h3 className="text-lg font-bold text-white mb-2 font-sans" data-id="element-593">
                {currentStepData.title}
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed" data-id="element-594">
                {currentStepData.description}
              </p>
            </div>
            {/* Footer */}
            <div className="px-5 py-3 bg-slate-950/50 border-t border-slate-800 flex items-center justify-between" data-id="element-595">
              <button onClick={skipDemo} className="text-xs text-slate-400 hover:text-white transition-colors" data-id="element-596">
                Skip Demo
              </button>
              <button onClick={nextStep} className="flex items-center gap-2 px-3 py-1.5 bg-sprBlue hover:bg-sprBlue/90 text-white text-sm rounded-lg transition-colors" data-id="element-597">
                Next
                <ArrowRightIcon className="w-3 h-3" data-id="element-598" />
              </button>
            </div>
          </div>
          {/* Arrow pointing to target */}
          {targetRect && <div className="absolute w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-slate-900" style={{
          top: -8,
          left: '50%',
          transform: 'translateX(-50%)'
        }} data-id="element-599" />}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}